import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getAuthors } from "../../helpers/authors.helper";
import { getCitation } from "../../helpers/citation.helper";
import { useAuth } from "@clerk/clerk-react";
import Tooltip from "../tooltip/Tooltip";

const copiedKeyframes = `
@keyframes copied-pop-in {
  0%   { opacity: 0; transform: translateY(6px) scale(0.85); }
  60%  { opacity: 1; transform: translateY(-2px) scale(1.05); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes copied-pop-out {
  0%   { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0; transform: translateY(-6px) scale(0.85); }
}
@keyframes check-draw {
  0%   { stroke-dashoffset: 30; opacity: 0; }
  40%  { opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 1; }
}
`;

function ClipboardIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline
        points="20 6 9 17 4 12"
        strokeDasharray="30"
        style={{ animation: "check-draw 0.35s ease forwards" }}
      />
    </svg>
  );
}

/** Plain text suitable for clipboard (strips citation HTML tags). */
function citationHtmlToPlain(html) {
  if (!html || typeof html !== "string") return "";
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.textContent || "").replace(/\u00a0/g, " ").trim();
}

function Result({ bookData, onSaveToProject }) {
  const { t } = useTranslation();
  const { isSignedIn } = useAuth();
  const [copied, setCopied] = useState(false);

  const citationHtml = getCitation(bookData) || "";

  const copyCitationToClipboard = async () => {
    const text = citationHtmlToPlain(citationHtml);
    if (!text || copied) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        /* ignore */
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative w-full px-5 pt-3 pb-5 shadow-result rounded-result mb-6 bg-mainWhite border border-mainDark/30 transition-shadow duration-200 hover:shadow-result-hover">
      <style>{copiedKeyframes}</style>
      {isSignedIn && onSaveToProject ? (
        <button
          type="button"
          onClick={() => onSaveToProject(bookData._id)}
          className="absolute right-4 top-4 rounded-full border border-mainDark/20 bg-mainWhite px-3 py-1 text-xs uppercase text-mainDark shadow-search transition hover:border-mainBlue hover:text-mainBlue"
          title={t("result.saveTooltip")}
        >
          {t("result.save")}
        </button>
      ) : null}
      {/* AUTHORs */}
      <p className="font-light text-mainBlue">
        {bookData.authors.length > 0
          ? getAuthors(bookData.authors)
          : bookData.archive}
      </p>
      {/* TITLE */}
      {bookData.url == "" || bookData.url == null ? (
        <h2
          className={`text-mainDark text-2xl font-normal py-2 ${
            isSignedIn && onSaveToProject ? "pr-24" : ""
          }`}
        >
          {bookData.title ? bookData.title : bookData.trust}
        </h2>
      ) : (
        <a
          href={bookData.url}
          target="_blank"
          rel="noreferrer"
          className={`block text-mainDark text-2xl underline font-normal py-2 ${
            isSignedIn && onSaveToProject ? "pr-24" : ""
          }`}
        >
          {bookData.title}
        </a>
      )}
      <p className=" text-mainDark font-light">
        {(bookData.citation_type == 1 ||
          bookData.citation_type == 2 ||
          bookData.citation_type == 3) &&
          bookData.year}
        {bookData.citation_type == 4 && bookData.parent_article}
        {bookData.citation_type == 5 && bookData.publisher}
        {bookData.citation_type == 6 && bookData.publisher}
        {bookData.citation_type == 7 && bookData.doc_number}
      </p>
      <div className="flex flex-row flex-wrap gap-3 my-3">
        {(bookData.keywords || []).map((keyword, index) => (
          <div
            className="rounded-md py-1 px-3 bg-mainBlue text-onPrimary uppercase"
            key={index}
          >
            {keyword}
          </div>
        ))}
      </div>

      <div className="mb-1 flex flex-row items-start gap-2">
        <p
          className="min-w-0 text-mainDark font-light mt-0.5"
          dangerouslySetInnerHTML={{ __html: citationHtml }}
        />
        {citationHtml ? (
          <div className="relative -mt-0.5 shrink-0 flex items-center">
            <Tooltip content={copied ? null : t("result.copyCitation")}>
              <button
                type="button"
                onClick={copyCitationToClipboard}
                className={`relative rounded-md p-1.5 transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mainBlue ${
                  copied
                    ? "bg-mainBlue/15 text-mainBlue dark:bg-mainAccent/20 dark:text-mainAccent"
                    : "text-mainDark hover:bg-mainBlue/15 hover:text-mainBlue dark:hover:bg-mainAccent/20 dark:hover:text-mainAccent"
                }`}
                aria-label={copied ? t("result.copied") : t("result.copyCitation")}
              >
                <span
                  className="block transition-all duration-200"
                  style={{
                    opacity: copied ? 0 : 1,
                    transform: copied ? "scale(0.5) rotate(-10deg)" : "scale(1) rotate(0deg)",
                    position: copied ? "absolute" : "relative",
                  }}
                >
                  <ClipboardIcon className="h-5 w-5" />
                </span>
                <span
                  className="block transition-all duration-200"
                  style={{
                    opacity: copied ? 1 : 0,
                    transform: copied ? "scale(1) rotate(0deg)" : "scale(0.5) rotate(10deg)",
                    position: copied ? "relative" : "absolute",
                  }}
                >
                  <CheckIcon className="h-5 w-5" />
                </span>
              </button>
            </Tooltip>
            {copied && (
              <span
                className="ml-1 text-xs font-medium text-mainBlue dark:text-mainAccent pointer-events-none select-none"
                style={{ animation: "copied-pop-in 0.3s ease forwards" }}
              >
                {t("result.copied")}
              </span>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default Result;
